import * as THREE from "three";
import { normsFromSphereHitPoint } from "../../../lib/equirectSphereNorm.ts";

const PICK_GRID = 3;

/**
 * Raycast from a screen click on the patrol 360° canvas → equirect (normX, normY).
 * Matches wankhede-360 / Pano360WebGL multi-sample pick on the inner sphere mesh.
 */
export function samplePatrolEquirectNormFromClient(
  canvas,
  camera,
  sphereMesh,
  clientX,
  clientY,
) {
  if (!canvas || !camera || !sphereMesh) return null;

  camera.updateMatrixWorld(true);
  sphereMesh.updateMatrixWorld(true);

  const rect = canvas.getBoundingClientRect();
  const w = Math.max(rect.width, 1);
  const h = Math.max(rect.height, 1);
  const ndcCx = ((clientX - rect.left) / w) * 2 - 1;
  const ndcCy = -((clientY - rect.top) / h) * 2 + 1;
  const stepX = (PICK_GRID * 2) / w;
  const stepY = (PICK_GRID * 2) / h;

  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  let sumSin = 0;
  let sumCos = 0;
  let sumY = 0;
  let n = 0;

  for (let j = -1; j <= 1; j++) {
    for (let i = -1; i <= 1; i++) {
      ndc.x = THREE.MathUtils.clamp(ndcCx + i * stepX, -1, 1);
      ndc.y = THREE.MathUtils.clamp(ndcCy + j * stepY, -1, 1);
      raycaster.setFromCamera(ndc, camera);
      const hit = raycaster.intersectObject(sphereMesh, false)[0];
      if (!hit?.point) continue;
      const norms = normsFromSphereHitPoint(
        hit.point.x,
        hit.point.y,
        hit.point.z,
        hit.uv,
      );
      if (!norms) continue;
      const ang = norms.normX * Math.PI * 2;
      sumSin += Math.sin(ang);
      sumCos += Math.cos(ang);
      sumY += norms.normY;
      n++;
    }
  }

  if (n === 0) return null;

  let phiMean = Math.atan2(sumSin / n, sumCos / n);
  if (phiMean < 0) phiMean += Math.PI * 2;

  return {
    normX: THREE.MathUtils.clamp(phiMean / (Math.PI * 2), 0, 1),
    normY: THREE.MathUtils.clamp(sumY / n, 0, 1),
  };
}
